import { useAuth } from "@/auth/AuthProvider";
import { ForcedMFAEnrollment } from "@/auth/ForcedMFAEnrollment";
import { FactorPicker } from "@/auth/FactorPicker";
import { MFAVerification } from "@/auth/MFAVerification";
import {
  pickInitialFactorId,
  rememberFactorId,
  type TotpFactor,
  toTotpFactors,
  verifyTotpCode,
} from "@/auth/mfaFactors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpiredFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("reason") === "session_expired",
    [location.search],
  );
  const authContext = useAuth();
  const {
    session,
    authStateReady,
    mfaRequired,
    mfaFactors,
    forceMFAEnrollment,
    isInviteOrRecoveryFlow: showSetPassword,
    mfaRequiredForPasswordReset: needsMFAForPasswordReset,
    mfaCheckFailed,
    retryMFACheck,
    redirectPathAfterLogin,
    navigateToAppHome,
    clearForceMFAEnrollment,
  } = authContext;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [resetFactors, setResetFactors] = useState<TotpFactor[]>([]);
  const [resetFactorId, setResetFactorId] = useState("");
  const [retryingMFACheck, setRetryingMFACheck] = useState(false);

  useEffect(() => {
    if (!needsMFAForPasswordReset) return;

    let cancelled = false;

    const loadFactors = async () => {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors();
        const factors = toTotpFactors(data?.totp ?? []);
        if (error || factors.length === 0) throw error ?? new Error("No MFA devices");

        if (cancelled) return;
        setResetFactors(factors);
        setResetFactorId(pickInitialFactorId(factors));
      } catch {
        if (cancelled) return;
        toast.error("Could not load your MFA devices");
      }
    };

    loadFactors();

    return () => {
      cancelled = true;
    };
  }, [needsMFAForPasswordReset]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if MFA is required; if so, auth listener will update session and context will show MFA screen
      if (data.session) {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const hasMFA = factors?.totp && factors.totp.length > 0;

        if (hasMFA) {
          const { data: assuranceLevel } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

          if (assuranceLevel?.currentLevel === "aal1") {
            // MFA required: auth listener updates session, provider sets mfaRequired/mfaFactors
            return;
          }
        }
        // No MFA or MFA already verified: auth listener will redirect by role
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("You have been logged out.");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Handle set password
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    // Password validation: at least 8 chars, 1 number, 1 symbol, 1 lowercase, 1 uppercase
    // Note: Supabase has specific allowed special characters, so we validate against those
    const passwordRequirements = [
      { regex: /.{8,}/, message: "at least 8 characters" },
      { regex: /[a-z]/, message: "a lowercase letter" },
      { regex: /[A-Z]/, message: "an uppercase letter" },
      { regex: /[0-9]/, message: "a number" },
      {
        regex: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/,
        message: "a special character (!@#$%^&*()_+-=[]{};':\"|<>?,./`~)",
      },
    ];
    const failed = passwordRequirements.filter((req) => !req.regex.test(newPassword));
    if (failed.length > 0) {
      toast.error(`Password must contain ${failed.map((f) => f.message).join(", ")}.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      // Password updated successfully - user is now logged in with new password
      toast.success("Password updated successfully! You are now signed in.");

      navigateToAppHome();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  // Handle MFA verification for password reset
  const handleMFAVerificationForPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode) {
      toast.error("Please enter your MFA code.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await verifyTotpCode({ factorId: resetFactorId, code: mfaCode });
      if (error) {
        toast.error(error);
        return;
      }

      rememberFactorId(resetFactorId);
      toast.success("MFA verified successfully. You can now set your new password.");
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Please enter your email address.");
      return;
    }

    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth#type=recovery`,
      });

      if (error) throw error;

      toast.success("Password reset link sent! Check your email.");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send reset email");
    } finally {
      setResetLoading(false);
    }
  };

  const handleRetryMFACheck = async () => {
    setRetryingMFACheck(true);
    try {
      await retryMFACheck();
    } finally {
      setRetryingMFACheck(false);
    }
  };

  const handleForcedMFAComplete = () => {
    clearForceMFAEnrollment();
    toast.success("Your account is now protected with two-factor authentication");
  };

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  name="reset-email"
                  autoComplete="email"
                  placeholder="Email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={resetLoading}>
                  {resetLoading ? "Sending..." : "Send Reset Link"}
                </Button>
              </div>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show MFA verification for password reset
  if (needsMFAForPasswordReset) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>MFA Verification Required</CardTitle>
            <CardDescription>
              To reset your password, please verify your MFA code from your authenticator app.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleMFAVerificationForPasswordReset} className="space-y-4">
              <FactorPicker factors={resetFactors} value={resetFactorId} onChange={setResetFactorId} />
              <div className="space-y-2">
                <Input
                  type="text"
                  name="mfa-code"
                  placeholder="Enter MFA code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={loading || resetFactorId === ""}>
                  {loading ? "Verifying..." : "Verify MFA"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showMFAVerification = mfaRequired && mfaFactors.length > 0;

  if (session && mfaCheckFailed && !showMFAVerification) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>We couldn't check your security settings</CardTitle>
            <CardDescription>
              Something went wrong while we looked up your two-factor devices, so please try again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Button onClick={handleRetryMFACheck} className="w-full" disabled={retryingMFACheck}>
                Try again
              </Button>
              <Button onClick={handleSignOut} variant="outline" className="w-full" disabled={loading}>
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show set password form if invite/recovery
  if (showSetPassword) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Set Your Password</CardTitle>
            <CardDescription>Please set a password for your account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" />
                <Input
                  id="new-password"
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" />
                <Input
                  id="confirm-password"
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                Set Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show forced MFA enrollment if needed
  if (forceMFAEnrollment) {
    return <ForcedMFAEnrollment isOpen={forceMFAEnrollment} onComplete={handleForcedMFAComplete} />;
  }

  // Show loading while auth context is resolving (e.g. MFA state for current session)
  if (session && !authStateReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardContent className="flex flex-col items-center justify-center pt-6">
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show MFA verification if needed (from auth context)
  if (showMFAVerification) {
    return (
      <MFAVerification
        factors={mfaFactors}
        onSuccess={navigateToAppHome}
        onCancel={async () => {
          try {
            await supabase.auth.signOut();
          } finally {
            navigate("/auth");
          }
        }}
      />
    );
  }

  // If user is already logged in and MFA is complete, show logout option
  if (session && authStateReady && !mfaRequired) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Already Logged In</CardTitle>
            <CardDescription>You are currently logged in. Log out to sign in as a different user.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Logged in as: {session.user.email}</p>
              <Button onClick={handleSignOut} variant="outline" className="w-full" disabled={loading}>
                Log Out
              </Button>
              <Button onClick={() => navigate(redirectPathAfterLogin)} className="w-full">
                Continue to App
              </Button>
              <Button onClick={() => navigate("/settings")} variant="outline" className="w-full">
                Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle>Welcome</CardTitle>
          <CardDescription>
            {sessionExpiredFromUrl ? "Your session expired. Please sign in again." : "Sign in to your account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Forgot Password?
              </button>
            </div>
            <div className="space-y-2">
              <Button type="submit" className="w-full" disabled={loading}>
                Sign In
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
